import type { ReactNode } from "react";
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import { AlertTriangle, X } from "lucide-react";

import { cn } from "../lib/cn";
import { Button } from "./Button";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: ReactNode;
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "danger" | "neutral";
  pending?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  children,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "danger",
  pending = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const isDanger = tone === "danger";

  return (
    <AlertDialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !pending) onClose();
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 z-70 bg-overlay backdrop-blur-[2px]" />
        <AlertDialog.Content
          onEscapeKeyDown={(event) => {
            if (pending) event.preventDefault();
          }}
          className="fixed top-1/2 left-1/2 z-70 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-dialog border border-border bg-surface shadow-dialog"
        >
          <div className="flex items-center gap-4 border-b border-border px-5 py-4 sm:px-6">
            {(isDanger || children) && <div
              className={cn(
                "grid h-11 w-11 shrink-0 place-items-center rounded-full border",
                isDanger
                  ? "border-status-danger/20 bg-status-danger/10 text-status-danger"
                  : "border-border bg-surface-elevated text-primary",
              )}
            >
              <AlertTriangle className="h-5 w-5" aria-hidden />
            </div>}

            <div className="min-w-0 flex-1">
              <AlertDialog.Title className="text-base font-semibold tracking-tight text-primary">
                {title}
              </AlertDialog.Title>
            </div>

            <AlertDialog.Cancel asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Close dialog"
                className="text-muted"
                disabled={pending}
              >
                <X className="h-4 w-4" aria-hidden />
              </Button>
            </AlertDialog.Cancel>
          </div>

          <div className="space-y-4 px-5 py-5 sm:px-6 sm:py-6">
            <AlertDialog.Description className="text-sm leading-6 text-secondary">
              {description}
            </AlertDialog.Description>

            {children ? <div>{children}</div> : null}
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-border px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
            <AlertDialog.Cancel asChild>
              <Button variant="secondary" disabled={pending}>
                {cancelLabel}
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action asChild>
              <Button
                variant={isDanger ? "danger" : "primary"}
                onClick={(event) => {
                  event.preventDefault();
                  onConfirm();
                }}
                loading={pending}
                loadingLabel="Working…"
              >
                {confirmLabel}
              </Button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
